import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { usePageTitle } from "../../../hooks";

interface SeasonRow {
  id: number;
  year: number;
  country: number;
  country_name: string;
  variety: number;
  variety_name: string;
  production_closed: boolean;
  estimation_closed: boolean;
  created?: string;
  modified?: string;
}

interface SeasonFormValues {
  year: number;
  country: number;
  variety: number;
  production_closed: boolean;
  estimation_closed: boolean;
}

interface SelectRow {
  id: number;
  name: string;
}

/**
 * Admin CRUD for `catalogue.Season`. Each Season ties one country to
 * one variety for a given year, plus two booleans that mark when
 * production and estimation are closed for that campaign. The
 * (country, variety, year) triple is the business key — the backend
 * enforces uniqueness server-side too.
 */
export const SeasonsAdminList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [data, setData] = useState<SeasonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<SelectRow[]>([]);
  const [varieties, setVarieties] = useState<SelectRow[]>([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<SeasonRow | null>(null);
  const [form] = Form.useForm<SeasonFormValues>();
  const [saving, setSaving] = useState(false);
  // Backend validation errors surfaced inside the modal (instead of
  // — or in addition to — a toast). One per line.
  const [editorErrors, setEditorErrors] = useState<string[]>([]);

  // Toolbar filters — applied client-side over the already-loaded
  // page (we ask the API for page_size=200, which is plenty for the
  // catalogue size).
  const [countryFilter, setCountryFilter] = useState<number | undefined>();
  const [yearFilter, setYearFilter] = useState<number | undefined>();

  usePageTitle(t("seasons.title", "Campañas"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<{ data?: SeasonRow[]; results?: SeasonRow[] }>(
        "/v1/admin/seasons/?page_size=200",
      );
      const list =
        (r.data as any)?.data ??
        (r.data as any)?.results ??
        (Array.isArray(r.data) ? (r.data as any) : []);
      setData(list as SeasonRow[]);
    } catch {
      notify.error(t("seasons.load_error", "Error al cargar las campañas"));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  // Country + variety lookups for the create/edit form. Both lists
  // are small (one-shot) so we keep them in component state instead
  // of paging on demand.
  const loadDropdowns = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([
        apiClient.get<any>("/countries/?page_size=500"),
        apiClient.get<any>("/v1/varieties/?page_size=500"),
      ]);
      const cs =
        c.data?.data ?? c.data?.results ?? (Array.isArray(c.data) ? c.data : []);
      const vs =
        v.data?.data ?? v.data?.results ?? (Array.isArray(v.data) ? v.data : []);
      setCountries(cs as SelectRow[]);
      setVarieties(vs as SelectRow[]);
    } catch {
      notify.error(
        t(
          "seasons.dropdowns_error",
          "No se pudieron cargar países / variedades",
        ),
      );
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
    void loadDropdowns();
  }, [load, loadDropdowns]);

  // ----- CRUD ---------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    setEditorErrors([]);
    form.resetFields();
    form.setFieldsValue({
      year: new Date().getFullYear(),
      production_closed: false,
      estimation_closed: false,
    } as SeasonFormValues);
    setEditorOpen(true);
  };

  const openEdit = (row: SeasonRow) => {
    setEditing(row);
    setEditorErrors([]);
    form.setFieldsValue({
      year: row.year,
      country: row.country,
      variety: row.variety,
      production_closed: row.production_closed,
      estimation_closed: row.estimation_closed,
    });
    setEditorOpen(true);
  };

  // Flatten any DRF-style validation response into a string[] so we
  // can show every backend error verbatim. Handles all the shapes
  // we actually see in this app:
  //   { detail: "msg" }                       ← generic / auth
  //   { non_field_errors: ["msg"] }           ← serializer.validate()
  //   { field: ["msg"] }                      ← serializer.field_validate
  //   { field: { nested: ["msg"] } }          ← nested errors
  // Anything we can't recognise falls through to the JSON dump so
  // the admin still sees *something*.
  const flattenErrors = (payload: any): string[] => {
    if (payload == null) return [];
    if (typeof payload === "string") return [payload];
    if (Array.isArray(payload))
      return payload.flatMap((p) => flattenErrors(p));
    if (typeof payload === "object") {
      const out: string[] = [];
      for (const [k, v] of Object.entries(payload)) {
        const lines = flattenErrors(v);
        for (const line of lines) {
          // Hide the technical key for the conventional DRF buckets,
          // surface it as a prefix for per-field errors so the admin
          // knows which input is complaining.
          if (k === "detail" || k === "non_field_errors") {
            out.push(line);
          } else {
            out.push(`${k}: ${line}`);
          }
        }
      }
      return out;
    }
    return [String(payload)];
  };

  const submitEditor = async () => {
    setEditorErrors([]);
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await apiClient.patch(`/v1/admin/seasons/${editing.id}/`, values);
        notify.success(t("seasons.update_success", "Campaña actualizada"));
      } else {
        await apiClient.post("/v1/admin/seasons/", values);
        notify.success(t("seasons.create_success", "Campaña creada"));
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      const lines = flattenErrors(e?.response?.data);
      const finalLines =
        lines.length > 0
          ? lines
          : [t("seasons.save_error", "No se pudo guardar la campaña")];
      // Persistent in-modal alert so the message stays visible until
      // the admin fixes the inputs. Toast as backup for the case
      // when the modal is dismissed externally.
      setEditorErrors(finalLines);
      notify.error(finalLines[0]);
    } finally {
      setSaving(false);
    }
  };

  // Distinct year list derived from the rows we have — keeps the
  // filter Select self-updating without an extra request.
  const yearOptions = useMemo(() => {
    const set = new Set<number>();
    for (const row of data) set.add(row.year);
    return Array.from(set)
      .sort((a, b) => b - a)
      .map((y) => ({ value: y, label: String(y) }));
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (countryFilter !== undefined && r.country !== countryFilter) return false;
      if (yearFilter !== undefined && r.year !== yearFilter) return false;
      return true;
    });
  }, [data, countryFilter, yearFilter]);

  // ----- table --------------------------------------------------------

  const columns = [
    {
      title: t("seasons.year", "Año"),
      dataIndex: "year",
      key: "year",
      width: 100,
      sorter: (a: SeasonRow, b: SeasonRow) => a.year - b.year,
      defaultSortOrder: "descend" as const,
      render: (v: number) => <strong>{v}</strong>,
    },
    {
      title: t("seasons.country", "País"),
      dataIndex: "country_name",
      key: "country_name",
      sorter: (a: SeasonRow, b: SeasonRow) =>
        (a.country_name || "").localeCompare(b.country_name || ""),
      render: (v: string) => v || "—",
    },
    {
      title: t("seasons.variety", "Variedad"),
      dataIndex: "variety_name",
      key: "variety_name",
      sorter: (a: SeasonRow, b: SeasonRow) =>
        (a.variety_name || "").localeCompare(b.variety_name || ""),
      render: (v: string) => v || "—",
    },
    {
      title: t("seasons.production_closed", "Producción cerrada"),
      dataIndex: "production_closed",
      key: "production_closed",
      width: 180,
      align: "center" as const,
      sorter: (a: SeasonRow, b: SeasonRow) =>
        Number(a.production_closed) - Number(b.production_closed),
      render: (v: boolean) =>
        v ? (
          <Tag color="green">{t("common.yes", "Sí")}</Tag>
        ) : (
          <Tag color="default">{t("common.no", "No")}</Tag>
        ),
    },
    {
      title: t("seasons.estimation_closed", "Estimación cerrada"),
      dataIndex: "estimation_closed",
      key: "estimation_closed",
      width: 180,
      align: "center" as const,
      sorter: (a: SeasonRow, b: SeasonRow) =>
        Number(a.estimation_closed) - Number(b.estimation_closed),
      render: (v: boolean) =>
        v ? (
          <Tag color="green">{t("common.yes", "Sí")}</Tag>
        ) : (
          <Tag color="default">{t("common.no", "No")}</Tag>
        ),
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 100,
      align: "right" as const,
      fixed: "right" as const,
      render: (_: unknown, row: SeasonRow) => (
        <Space size="small">
          <Tooltip title={t("common.edit", "Editar")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<CalendarOutlined />}
        accent="#13C2C2"
        title={t("seasons.title", "Campañas")}
        subtitle={t(
          "seasons.subtitle",
          "Catálogo de campañas de producción. Cada campaña combina un país, una variedad y un año.",
        )}
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("seasons.create", "Nueva campaña")}
            </Button>
          </Space>
        }
      />

      <Space
        size="middle"
        wrap
        style={{ width: "100%", marginBottom: 16 }}
      >
        <Select
          allowClear
          placeholder={t("seasons.filter_country", "Filtrar por país")}
          value={countryFilter}
          onChange={(v) => setCountryFilter(v)}
          options={countries.map((c) => ({ value: c.id, label: c.name }))}
          style={{ minWidth: 220 }}
          showSearch
          optionFilterProp="label"
        />
        <Select
          allowClear
          placeholder={t("seasons.filter_year", "Filtrar por año")}
          value={yearFilter}
          onChange={(v) => setYearFilter(v)}
          options={yearOptions}
          style={{ minWidth: 140 }}
          showSearch
          optionFilterProp="label"
        />
        {(countryFilter !== undefined || yearFilter !== undefined) && (
          <Button
            type="text"
            onClick={() => {
              setCountryFilter(undefined);
              setYearFilter(undefined);
            }}
          >
            {t("common.clearFilters", "Limpiar filtros")}
          </Button>
        )}
      </Space>

      <Table<SeasonRow>
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 960 }}
      />

      {/* Create / Edit modal */}
      <Modal
        title={
          editing
            ? t("seasons.edit_title", "Editar campaña")
            : t("seasons.create_title", "Nueva campaña")
        }
        open={editorOpen}
        onCancel={() => (saving ? undefined : setEditorOpen(false))}
        onOk={submitEditor}
        confirmLoading={saving}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={520}
      >
        {editorErrors.length > 0 && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setEditorErrors([])}
            style={{ marginBottom: 16 }}
            message={t("seasons.save_error", "No se pudo guardar la campaña")}
            description={
              editorErrors.length === 1 ? (
                editorErrors[0]
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {editorErrors.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              )
            }
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("seasons.year", "Año")}
            name="year"
            rules={[
              {
                required: true,
                message: t("seasons.year_required", "El año es obligatorio"),
              },
            ]}
          >
            <InputNumber
              min={2000}
              max={2100}
              style={{ width: "100%" }}
              autoFocus
            />
          </Form.Item>

          <Form.Item
            label={t("seasons.country", "País")}
            name="country"
            rules={[
              {
                required: true,
                message: t(
                  "seasons.country_required",
                  "Selecciona un país",
                ),
              },
            ]}
          >
            <Select
              placeholder={t("seasons.country_placeholder", "Seleccionar país")}
              showSearch
              options={countries.map((c) => ({ value: c.id, label: c.name }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label={t("seasons.variety", "Variedad")}
            name="variety"
            rules={[
              {
                required: true,
                message: t(
                  "seasons.variety_required",
                  "Selecciona una variedad",
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                "seasons.variety_placeholder",
                "Seleccionar variedad",
              )}
              showSearch
              options={varieties.map((v) => ({ value: v.id, label: v.name }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label={t("seasons.production_closed", "Producción cerrada")}
            name="production_closed"
            valuePropName="checked"
            tooltip={t(
              "seasons.production_closed_help",
              "Indica que la producción para esta campaña ya está cerrada.",
            )}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t("seasons.estimation_closed", "Estimación cerrada")}
            name="estimation_closed"
            valuePropName="checked"
            tooltip={t(
              "seasons.estimation_closed_help",
              "Indica que la estimación para esta campaña ya está cerrada.",
            )}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default SeasonsAdminList;
